import React from "react";
import {
  Box,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Checkbox,
  Button,
  Menu,
  Skeleton,
} from "@mui/material";

const MAX_WIDTH = 200;

/**
 * CheckboxFilterMenu
 *
 * A dropdown menu for selecting one or more filter values via checkboxes.
 * Supports flat lists, grouped lists, icons/emojis and an optional footer action link.
 * Renders one of three states: loading (skeletons), loaded with no options (an
 * empty message, unless a footer action covers it) and loaded with options.
 *
 * @param {Object} props
 * @param {Array<{ value: string|number, label: string, icon?: ReactNode, emoji?: string }>
 *   | Object.<string, string[]>} props.options
 *   Either a flat array of option objects, or a grouped object where keys are group labels
 *   and values are arrays of string option values.
 * @param {Array<string|number>} props.selectedOptions - Currently selected values
 * @param {(values: Array<string|number>) => void} props.onChange - Fired when selection changes
 * @param {() => void} props.onApply - Fired when Apply button is clicked
 * @param {() => void} props.onClear - Fired when Clear button is clicked
 * @param {HTMLElement|null} props.anchorEl - Anchor element for the menu
 * @param {() => void} props.onClose - Fired when the menu closes
 * @param {boolean} [props.loading=false] - If true, renders skeleton items instead of options
 * @param {{ label: string, onClick: () => void }} [props.footerAction] - Optional action rendered
 *   as the last item in the list, styled like a menu item but without a checkbox.
 * @param {boolean} [props.selectAll=false] - Lead a flat list with a "Select all" item that
 *   ticks (or, when all are ticked, unticks) every option.
 */
const CheckboxFilterMenu = ({
  options,
  selectedOptions,
  onChange,
  onApply,
  onClear,
  anchorEl,
  onClose,
  loading = false,
  footerAction,
  selectAll = false,
}) => {
  const isGrouped = !Array.isArray(options);
  const hasOptions = isGrouped
    ? Object.keys(options).length > 0
    : options.length > 0;

  const toggleSingle = (val) => {
    const exists = selectedOptions.includes(val);
    const next = exists
      ? selectedOptions.filter((s) => s !== val)
      : [...selectedOptions, val];
    onChange(next);
  };

  // Every value a flat list offers, for "Select all"
  const flatValues = isGrouped
    ? []
    : options.map((option) => option.value ?? option);
  const allFlatSelected =
    flatValues.length > 0 &&
    flatValues.every((v) => selectedOptions.includes(v));
  const someFlatSelected =
    !allFlatSelected && flatValues.some((v) => selectedOptions.includes(v));
  const toggleAll = () => onChange(allFlatSelected ? [] : flatValues);

  const toggleGroup = (groupedList) => {
    const allSelected = groupedList.every((g) => selectedOptions.includes(g));
    const next = allSelected
      ? selectedOptions.filter((s) => !groupedList.includes(s))
      : Array.from(new Set([...selectedOptions, ...groupedList]));
    onChange(next);
  };

  return (
    <Menu
      anchorEl={anchorEl}
      open={Boolean(anchorEl)}
      onClose={onClose}
      slotProps={{
        paper: {
          elevation: 0,
          sx: {
            overflow: "auto",
            maxHeight: 350,
            filter: "drop-shadow(0px 2px 8px rgba(0,0,0,0.32))",
            mt: 1.5,
            "&::before": {
              content: '""',
              display: "block",
              position: "absolute",
              top: 0,
              left: 14,
              width: 10,
              height: 10,
              bgcolor: "background.paper",
              transform: "translateY(-50%) rotate(45deg)",
              zIndex: 0,
            },
          },
        },
      }}
    >
      <Box sx={{ maxHeight: 250, overflow: "auto" }}>
        {!loading && !hasOptions && !footerAction && (
          <MenuItem dense disabled>
            <ListItemText primary="Nothing to filter by yet" />
          </MenuItem>
        )}

        {loading
          ? // Skeleton loading state
            [1, 2, 3].map((i) => (
              <MenuItem key={i} dense disabled>
                <ListItemIcon>
                  <Skeleton
                    variant="rectangular"
                    width={18}
                    height={18}
                    sx={{ borderRadius: 0.5 }}
                  />
                </ListItemIcon>
                <Skeleton variant="text" width={120} />
              </MenuItem>
            ))
          : isGrouped
            ? // Grouped options
              Object.entries(options).map(([groupLabel, groupedList]) => {
                const allSelected = groupedList.every((g) =>
                  selectedOptions.includes(g),
                );
                const someSelected =
                  groupedList.some((g) => selectedOptions.includes(g)) &&
                  !allSelected;

                return (
                  <React.Fragment key={groupLabel}>
                    <MenuItem
                      dense
                      onClick={() => toggleGroup(groupedList)}
                      sx={{ bgcolor: "background.paper" }}
                    >
                      <ListItemIcon>
                        <Checkbox
                          checked={allSelected}
                          indeterminate={someSelected}
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleGroup(groupedList);
                          }}
                        />
                      </ListItemIcon>
                      <ListItemText
                        primary={groupLabel}
                        primaryTypographyProps={{
                          fontWeight: "bold",
                          noWrap: true,
                          sx: { maxWidth: MAX_WIDTH },
                        }}
                      />
                    </MenuItem>
                    {groupedList.map((option) => (
                      <MenuItem
                        key={option}
                        dense
                        onClick={() => toggleSingle(option)}
                        sx={{ pl: 4 }}
                      >
                        <ListItemIcon>
                          <Checkbox
                            checked={selectedOptions.includes(option)}
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleSingle(option);
                            }}
                          />
                        </ListItemIcon>
                        <ListItemText
                          primary={option}
                          primaryTypographyProps={{
                            noWrap: true,
                            sx: { maxWidth: MAX_WIDTH },
                          }}
                        />
                      </MenuItem>
                    ))}
                  </React.Fragment>
                );
              })
            : // Flat options, led by "Select all" where asked for
              [
                selectAll && hasOptions && (
                  <MenuItem key="select-all" dense onClick={toggleAll}>
                    <ListItemIcon>
                      <Checkbox
                        checked={allFlatSelected}
                        indeterminate={someFlatSelected}
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleAll();
                        }}
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary="Select all"
                      primaryTypographyProps={{ fontWeight: "bold" }}
                    />
                  </MenuItem>
                ),
                ...options.map((option) => {
                  const value = option.value ?? option;
                  const label = option.label ?? option;
                  const icon =
                    option.icon ??
                    (option.emoji ? (
                      <span role="img" aria-label={label}>
                        {option.emoji}
                      </span>
                    ) : null);
                  return (
                    <MenuItem
                      key={value}
                      onClick={() => toggleSingle(value)}
                      dense
                    >
                      <ListItemIcon>
                        <Checkbox
                          checked={selectedOptions.includes(value)}
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleSingle(value);
                          }}
                        />
                      </ListItemIcon>
                      {icon && (
                        <ListItemIcon sx={{ minWidth: 32, ml: 1, mr: -1 }}>
                          {icon}
                        </ListItemIcon>
                      )}
                      <ListItemText
                        primary={label}
                        primaryTypographyProps={{
                          noWrap: true,
                          sx: { maxWidth: MAX_WIDTH },
                        }}
                      />
                    </MenuItem>
                  );
                }),
              ]}

        {/* Footer action — rendered as last list item, no checkbox */}
        {!loading && footerAction && (
          <MenuItem
            dense
            onClick={() => {
              footerAction.onClick();
              onClose();
            }}
          >
            <ListItemText primary={footerAction.label} />
          </MenuItem>
        )}
      </Box>

      {/* Apply / Clear buttons — hidden when there's nothing to select */}
      {!loading && hasOptions && (
        <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2, mx: 2 }}>
          <Button
            variant="outlined"
            color="primary"
            size="small"
            onClick={onClear}
            sx={{ mr: 1 }}
          >
            Clear
          </Button>
          <Button
            variant="contained"
            color="primary"
            size="small"
            onClick={onApply}
            sx={{ ml: 1 }}
          >
            Apply
          </Button>
        </Box>
      )}
    </Menu>
  );
};

export default CheckboxFilterMenu;
